<template>
  <Admin style="background: #f2f2f2">
    <v-main>
      <v-container fluid>
        <v-card class="rounded-xl">
          <v-card-text class="tw-p-8">
            <v-breadcrumbs
              :items="breadcrumbItems"
              class="tw-px-0 tw-pt-0 tw-pb-4"
            >
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :to="item.to"
                  :disabled="item.disabled"
                  :exact="true"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>

            <div class="tw-flex tw-items-center tw-gap-2 tw-mb-6">
              <h2 class="tw-text-xl tw-font-bold tw-text-black tw-mb-0">
                ตั้งค่า Lao eTax
              </h2>
              <v-chip x-small color="grey lighten-2">อ่านอย่างเดียว ยกเว้นสวิตช์ Excel และส่งอัตโนมัติ</v-chip>
            </div>

            <div v-if="loading" class="tw-py-10 tw-text-center">
              <v-progress-circular indeterminate color="#6ec39b" />
            </div>

            <div v-else-if="error" class="tw-py-6">
              <v-alert type="error" text dense class="tw-mb-0">
                อ่าน Settings/laoEtax ไม่สำเร็จ — {{ error }}
              </v-alert>
            </div>

            <div v-else>
              <!-- What THIS company actually gets. The raw map below is the same
                   data, but a support question is almost always "why is this
                   company behaving like that", so the resolved answer comes first. -->
              <div class="tw-text-xs tw-text-gray-500 tw-mb-1">บริษัทที่เปิดอยู่</div>
              <div class="tw-font-bold tw-mb-3">
                {{ currentCompanyName }}
                <span class="tw-text-xs tw-text-gray-400 tw-font-normal tw-ml-1">{{ companyID }}</span>
              </div>

              <v-simple-table dense class="tw-mb-6 tw-border tw-rounded">
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td style="width: 190px">Gateway ที่ใช้ยื่น</td>
                      <td>
                        <v-chip
                          x-small
                          :color="resolved.env === 'prod' ? 'error' : 'grey lighten-1'"
                          :text-color="resolved.env === 'prod' ? 'white' : 'grey darken-3'"
                        >
                          {{ resolved.env === 'prod' ? 'PROD (ระบบจริง)' : 'DEV (ทดสอบ)' }}
                        </v-chip>
                        <span
                          v-if="resolved.env === 'unknown'"
                          class="tw-text-xs tw-text-orange-700"
                        >
                          ตัดสินที่ฝั่ง server (ETAX_LAO_ENV) — หน้านี้อ่านไม่ได้
                        </span>
                        <span class="tw-text-xs tw-text-gray-500 tw-ml-2">
                          มาจาก {{ resolved.envSource }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>ประเภทเอกสารที่ยื่นได้</td>
                      <td>
                        <span v-if="resolved.docTypes.length === 0" class="tw-text-red-600">
                          [] — ยื่นไม่ได้เลย
                        </span>
                        <template v-else>
                          <v-chip
                            v-for="t in resolved.docTypes"
                            :key="t"
                            x-small
                            class="tw-mr-1"
                            color="grey lighten-3"
                          >
                            {{ docTypeLabel(t) }}
                          </v-chip>
                        </template>
                        <span class="tw-text-xs tw-text-gray-500 tw-ml-2">
                          มาจาก {{ resolved.docTypesSource }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>คอลัมน์เสริมในตาราง</td>
                      <td>
                        <span v-if="resolved.extraColumns.length === 0" class="tw-text-gray-500">
                          —
                        </span>
                        <v-chip
                          v-for="c in resolved.extraColumns"
                          :key="c"
                          x-small
                          class="tw-mr-1"
                          color="grey lighten-3"
                        >
                          {{ columnLabel(c) }}
                        </v-chip>
                        <span class="tw-text-xs tw-text-gray-500 tw-ml-2">
                          มาจาก {{ resolved.extraColumnsSource }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>ดาวน์โหลด Excel</td>
                      <td>
                        <div class="tw-flex tw-items-center tw-gap-2">
                          <v-switch
                            :input-value="resolved.export.enabled"
                            :loading="savingExport"
                            :disabled="savingExport"
                            color="#6ec39b"
                            hide-details
                            dense
                            class="tw-mt-0 tw-pt-0"
                            aria-label="เปิดหรือปิดการดาวน์โหลด Excel ของบริษัทนี้"
                            @change="setExport"
                          />
                          <span>{{ resolved.export.enabled ? "เปิด" : "ปิด" }}</span>
                          <span class="tw-text-xs tw-text-gray-500">
                            มาจาก {{ resolved.export.source }}
                          </span>
                        </div>
                        <div v-if="exportError" class="tw-text-xs tw-text-red-600 tw-mt-1">
                          {{ exportError }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>เอกสารจาก API ส่งกรมฯ อัตโนมัติ</td>
                      <td>
                        <div class="tw-flex tw-items-center tw-gap-2">
                          <v-switch
                            :input-value="resolved.autoIssue.enabled === true"
                            :loading="savingAutoIssue"
                            :disabled="savingAutoIssue"
                            color="#6ec39b"
                            hide-details
                            dense
                            class="tw-mt-0 tw-pt-0"
                            aria-label="เปิดหรือปิดการส่งเอกสารจาก API ไปกรมสรรพากรลาวโดยอัตโนมัติ"
                            @change="setAutoIssue"
                          />
                          <span v-if="resolved.autoIssue.enabled === null" class="tw-text-red-600">
                            ค่าไม่ถูกต้อง — API จะรับเอกสารไม่ได้
                          </span>
                          <span v-else>
                            {{ resolved.autoIssue.enabled ? "เปิด — ส่งทันทีที่รับ" : "ปิด — เก็บไว้รอกดส่งจากหน้าเอกสาร" }}
                          </span>
                          <span class="tw-text-xs tw-text-gray-500">
                            มาจาก {{ resolved.autoIssue.source }}
                          </span>
                        </div>
                        <div class="tw-text-xs tw-text-gray-500 tw-mt-1">
                          ERP ส่ง <code>autoIssue</code> ใน request เพื่อ override เป็นครั้ง ๆ ได้
                        </div>
                        <div v-if="autoIssueError" class="tw-text-xs tw-text-red-600 tw-mt-1">
                          {{ autoIssueError }}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div class="tw-text-xs tw-text-gray-500 tw-mb-1">
                ทุกบริษัทใน Settings/laoEtax
              </div>
              <v-simple-table dense class="tw-border tw-rounded">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">บริษัท</th>
                      <th class="text-left" style="width: 150px">env</th>
                      <th class="text-left" style="width: 180px">docTypes</th>
                      <th class="text-left" style="width: 150px">extraColumns</th>
                      <th class="text-left" style="width: 110px">Excel</th>
                      <th class="text-left" style="width: 110px">API อัตโนมัติ</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- A company filing to the live MoF gateway is the row an
                         engineer is looking for, so prod tints the whole row.
                         Which company is open is marked with a chip instead of
                         a second background colour: the two can coincide. -->
                    <tr
                      v-for="row in rows"
                      :key="row.companyID"
                      :class="row.env === 'prod' ? 'tw-bg-red-50' : ''"
                    >
                      <td class="tw-py-2">
                        <div class="tw-flex tw-items-center tw-gap-2">
                          <span class="tw-font-bold">{{ row.nameTH || "(ไม่มีชื่อไทย)" }}</span>
                          <v-chip
                            v-if="row.companyID === companyID"
                            x-small
                            color="#6ec39b"
                            text-color="white"
                          >
                            บริษัทนี้
                          </v-chip>
                        </div>
                        <div v-if="row.nameEN" class="tw-text-xs tw-text-gray-600">
                          {{ row.nameEN }}
                        </div>
                        <div class="tw-text-xs tw-text-gray-400">{{ row.companyID }}</div>
                      </td>
                      <td>
                        <!-- Resolved, never blank: a company with no companyEnv
                             entry still files somewhere. Inherited values are
                             greyed and labelled so the table does not read as
                             though every company was configured by hand. -->
                        <v-chip
                          x-small
                          :color="envChip(row.env).color"
                          :text-color="envChip(row.env).text"
                        >
                          {{ envChip(row.env).label }}
                        </v-chip>
                        <div
                          v-if="!row.envExplicit"
                          class="tw-text-xs tw-text-gray-500 tw-mt-1"
                        >
                          {{ row.envSource }}
                        </div>
                      </td>
                      <td>
                        <span :class="row.docTypes === null ? 'tw-text-gray-500' : ''">
                          {{
                            row.docTypes === null
                              ? "— ตามค่าเริ่มต้น"
                              : "[" + row.docTypes.join(", ") + "]"
                          }}
                        </span>
                      </td>
                      <td>
                        <span :class="row.extraColumns === null ? 'tw-text-gray-500' : ''">
                          {{
                            row.extraColumns === null
                              ? "—"
                              : "[" + row.extraColumns.join(", ") + "]"
                          }}
                        </span>
                      </td>
                      <td>
                        <span :class="row.exportExplicit ? '' : 'tw-text-gray-500'">
                          {{ row.exportEnabled ? "เปิด" : "ปิด" }}
                        </span>
                      </td>
                      <td>
                        <span
                          :class="
                            row.autoIssue === null
                              ? 'tw-text-red-600'
                              : row.autoIssueExplicit
                              ? ''
                              : 'tw-text-gray-500'
                          "
                        >
                          {{ row.autoIssue === null ? "ค่าผิด" : row.autoIssue ? "เปิด" : "ปิด" }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div class="tw-mt-4 tw-text-xs tw-text-gray-600">
                ค่าเริ่มต้นของทุกบริษัทที่ไม่มีบรรทัดของตัวเอง —
                <b>defaultEnv:</b>
                {{ raw.defaultEnv || "(ไม่ได้ตั้ง → ใช้ ETAX_LAO_ENV ของ server)" }} ·
                <b>defaultDocTypes:</b>
                {{
                  raw.defaultDocTypes
                    ? "[" + raw.defaultDocTypes.join(", ") + "]"
                    : "(ไม่ได้ตั้ง → ยื่นได้ทุกประเภทที่รองรับ)"
                }}
                ·
                <b>defaultExtraColumns:</b>
                {{
                  raw.defaultExtraColumns
                    ? "[" + raw.defaultExtraColumns.join(", ") + "]"
                    : "(ไม่ได้ตั้ง → ไม่มีคอลัมน์เสริม)"
                }}
                ·
                <b>defaultApiAutoIssue:</b>
                {{
                  typeof raw.defaultApiAutoIssue === "undefined"
                    ? "(ไม่ได้ตั้ง → เปิด)"
                    : raw.defaultApiAutoIssue
                }}
              </div>

              <v-alert
                type="info"
                text
                dense
                class="tw-mt-4 tw-mb-0"
                style="font-size: 12px"
              >
                สวิตช์ดาวน์โหลด Excel และส่งอัตโนมัติจาก API เปลี่ยนได้จากหน้านี้
                (บันทึกผ่าน function ที่เขียนได้เฉพาะสองค่านี้ และเก็บประวัติไว้ใน
                SettingsHistory)
                ค่าอื่นแก้ได้จากเครื่องที่มี serviceAccount.json เท่านั้น —
                <code>scripts/setLaoDocTypes.js</code> และ
                <code>scripts/setLaoExtraColumns.js</code> (dry-run เป็นค่าเริ่มต้น)
                ตั้งใจให้ <code>companyEnv</code> เปลี่ยนยาก: ตั้งผิดจาก prod เป็น dev
                แปลว่าใบกำกับไม่ได้ถูกยื่นจริง โดยที่หน้าจอยังขึ้นว่าอนุมัติ
              </v-alert>
            </div>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
/**
 * Read-only view of Settings/laoEtax — the ตั้งค่า menu entry, engineers only.
 *
 * The exceptions are two per-company switches, each written through a function
 * that can change its own key and nothing else, and logs each change to
 * SettingsHistory:
 *   exportEnabled  (callSetLaoEtaxExport)    — the Excel download button
 *   apiAutoIssue   (callSetLaoEtaxAutoIssue) — whether ERP API pushes go to the
 *                  gateway at once or wait for a send from the documents table
 * A wrong value there costs a company a button or a delay, not a filing to the
 * wrong gateway, so it does not need the same friction as everything below.
 *
 * WHY read-only: firestore.rules exposes Settings for reading and grants no
 * write, so the client could not write it even if this offered to. That is
 * deliberate rather than an oversight — `companyEnv` decides whether a
 * company's invoices go to the real MoF gateway or the test one, and getting it
 * wrong is silent: the document still reaches "approved" in the UI while
 * nothing was filed with the tax authority. Changing it stays a decision made
 * on a machine with serviceAccount.json, through a script that dry-runs first.
 *
 * What this screen is for is the other half: answering "why is this company
 * behaving like that" without asking a developer to run a script and read out
 * the answer.
 *
 * The resolution below MIRRORS firebase/functions/src/libs/libLaoEnv.js
 * (resolveLaoEnv / resolveLaoDocTypes) and constants/laoEtaxColumns.js. A
 * viewer that resolves differently from the backend is worse than no viewer, so
 * when that file changes, change this too.
 *
 * The menu entry in settings.vue is the only way in, and it is gated on
 * isEngineer. The route itself is not a secret — everything shown here is
 * world-readable under firestore.rules `match /Settings` — but the gate keeps
 * it out of the way of customers and non-engineering staff.
 */
import Admin from "../../components/admin/admin-main-template";
import fc from "@/firebaseConfig";
import { SUPPORTED_LAO_DOC_TYPES } from "@/constants/laoEtaxDocTypes";
import { LAO_EXTRA_COLUMNS } from "@/constants/laoEtaxColumns";
import { exportEnabledFor } from "@/constants/laoEtaxExport";
import { apiAutoIssueFor } from "@/constants/laoEtaxAutoIssue";
import firebase from "firebase/app";
import "firebase/functions";

const DOC_TYPE_LABEL = {
  1: "1 TX ใบกำกับภาษี",
  4: "4 CR ใบลดหนี้",
  6: "6 RT ใบเสร็จ/กำกับภาษี",
};

export default {
  name: "SettingLaoEtax",
  components: { Admin },
  data() {
    return {
      companyID: this.$route.params.companyId,
      loading: false,
      error: "",
      raw: {},
      rows: [],
      savingExport: false,
      exportError: "",
      savingAutoIssue: false,
      autoIssueError: "",
      breadcrumbItems: [
        { text: "ตั้งค่า", disabled: false, to: { name: "Company.Settings" } },
        { text: "ตั้งค่า Lao eTax", disabled: true },
      ],
    };
  },
  computed: {
    // The rows already carry every company's name, including this one (load()
    // adds the current companyID to the set), so the header reuses that read
    // rather than taking a name prop that may or may not be populated yet.
    currentCompanyName() {
      const row = this.rows.find((r) => r.companyID === this.companyID);
      if (!row) return this.companyID;
      const parts = [row.nameTH, row.nameEN].filter(Boolean);
      return parts.length ? parts.join(" — ") : this.companyID;
    },
    // Mirrors libLaoEnv.resolveLaoEnv / resolveLaoDocTypes, including which
    // step of the fallback chain produced the answer — "prod" means something
    // different to an engineer depending on whether this company was named
    // explicitly or merely inherited the default.
    resolved() {
      const cid = (this.companyID || "").toString().trim();
      const out = {
        env: "unknown",
        envSource: "",
        docTypes: [],
        docTypesSource: "",
        extraColumns: [],
        extraColumnsSource: "",
        export: exportEnabledFor(this.raw, cid),
        autoIssue: apiAutoIssueFor(this.raw, cid),
      };

      const envMap = this.raw.companyEnv;
      if (cid && envMap && Object.prototype.hasOwnProperty.call(envMap, cid)) {
        out.env = this.normalizeEnv(envMap[cid]) || "unknown";
        out.envSource = 'companyEnv["' + cid + '"]';
      } else if (this.raw.defaultEnv) {
        out.env = this.normalizeEnv(this.raw.defaultEnv) || "unknown";
        out.envSource = "defaultEnv";
      } else {
        out.envSource = "ETAX_LAO_ENV ของ server";
      }

      const dtMap = this.raw.docTypes;
      if (cid && dtMap && Object.prototype.hasOwnProperty.call(dtMap, cid)) {
        out.docTypes = this.normalizeList(dtMap[cid]);
        out.docTypesSource = 'docTypes["' + cid + '"]';
      } else if (this.raw.defaultDocTypes) {
        out.docTypes = this.normalizeList(this.raw.defaultDocTypes);
        out.docTypesSource = "defaultDocTypes";
      } else {
        out.docTypes = SUPPORTED_LAO_DOC_TYPES.slice();
        out.docTypesSource = "ค่าเริ่มต้น (ไม่ได้ตั้ง)";
      }

      const ecMap = this.raw.extraColumns;
      if (cid && ecMap && Object.prototype.hasOwnProperty.call(ecMap, cid)) {
        out.extraColumns = Array.isArray(ecMap[cid]) ? ecMap[cid] : [];
        out.extraColumnsSource = 'extraColumns["' + cid + '"]';
      } else if (this.raw.defaultExtraColumns) {
        out.extraColumns = Array.isArray(this.raw.defaultExtraColumns)
          ? this.raw.defaultExtraColumns
          : [];
        out.extraColumnsSource = "defaultExtraColumns";
      } else {
        out.extraColumnsSource = "ค่าเริ่มต้น (ไม่ได้ตั้ง)";
      }

      return out;
    },
  },
  // Fresh read on every visit rather than anything cached: these values are
  // edited from a script outside the app, so a stale answer here would be worse
  // than a slow one.
  created() {
    this.load();
  },
  methods: {
    normalizeEnv(v) {
      const s = (v === null || typeof v === "undefined" ? "" : "" + v)
        .trim()
        .toLowerCase();
      return s === "dev" || s === "prod" ? s : "";
    },
    normalizeList(raw) {
      if (!Array.isArray(raw)) return [];
      const out = [];
      raw.forEach((v) => {
        const n = Number(v);
        if (!isNaN(n) && out.indexOf(n) === -1) out.push(n);
      });
      return out;
    },
    docTypeLabel(t) {
      return DOC_TYPE_LABEL[Number(t)] || "? " + t;
    },
    // prod is the one worth spotting from across the table — it means real
    // invoices reach the tax authority — so it gets the loud colour and dev
    // stays quiet. Anything else is a value the browser cannot resolve, which
    // is a third state rather than a shade of dev.
    envChip(env) {
      if (env === "prod") {
        return { color: "error", text: "white", label: "PROD" };
      }
      if (env === "dev") {
        return { color: "grey lighten-1", text: "grey darken-3", label: "DEV" };
      }
      return { color: "orange lighten-4", text: "orange darken-4", label: "?" };
    },
    columnLabel(id) {
      const col = LAO_EXTRA_COLUMNS.find((c) => c.id === id);
      return col ? col.label : id + " (เว็บไม่รู้จัก)";
    },
    // Through callSetLaoEtaxExport, the one write into Settings/laoEtax the
    // client can reach — it touches exportEnabled.<companyID> and nothing else.
    // The page re-reads afterwards rather than trusting its own guess, which is
    // also what puts the switch back when the save is refused.
    async setExport(enabled) {
      this.savingExport = true;
      this.exportError = "";
      try {
        const call = firebase.functions().httpsCallable("callSetLaoEtaxExport");
        const res = await call({ companyID: this.companyID, enabled: !!enabled });
        const r = (res && res.data) || {};
        if (!r.ok) this.exportError = "บันทึกไม่สำเร็จ — " + (r.message || r.errorType || "");
      } catch (e) {
        this.exportError = "บันทึกไม่สำเร็จ — " + ((e && e.message) || String(e));
      } finally {
        this.savingExport = false;
        await this.load();
      }
    },
    // Same shape as setExport, through callSetLaoEtaxAutoIssue — the one write
    // path to apiAutoIssue.<companyID>.
    async setAutoIssue(enabled) {
      this.savingAutoIssue = true;
      this.autoIssueError = "";
      try {
        const call = firebase.functions().httpsCallable("callSetLaoEtaxAutoIssue");
        const res = await call({ companyID: this.companyID, enabled: !!enabled });
        const r = (res && res.data) || {};
        if (!r.ok) this.autoIssueError = "บันทึกไม่สำเร็จ — " + (r.message || r.errorType || "");
      } catch (e) {
        this.autoIssueError = "บันทึกไม่สำเร็จ — " + ((e && e.message) || String(e));
      } finally {
        this.savingAutoIssue = false;
        await this.load();
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const snap = await fc.db.collection("Settings").doc("laoEtax").get();
        this.raw = snap.exists ? snap.data() || {} : {};

        // Union of every company named under any key, so a company configured
        // in one map but not the others is still listed rather than invisible.
        const ids = {};
        ["companyEnv", "docTypes", "extraColumns", "exportEnabled", "apiAutoIssue"].forEach((k) => {
          const m = this.raw[k];
          if (m && typeof m === "object") {
            Object.keys(m).forEach((id) => {
              ids[id] = true;
            });
          }
        });
        if (this.companyID) ids[this.companyID] = true;

        const list = Object.keys(ids).sort();
        const rows = await Promise.all(
          list.map(async (id) => {
            let nameTH = "";
            let nameEN = "";
            try {
              const co = await fc.companies.doc(id).get();
              const d = co.exists ? co.data() || {} : {};
              nameTH = d.nameTH || "";
              nameEN = d.nameEN || "";
            } catch (e) {
              nameTH = "(อ่านชื่อไม่ได้)";
            }
            const envMap = this.raw.companyEnv || {};
            const dtMap = this.raw.docTypes || {};
            const ecMap = this.raw.extraColumns || {};

            // Resolve rather than leave blank. A company with no companyEnv
            // entry is not "no environment" — it files to whatever defaultEnv
            // says, which is the fact an engineer scanning this table needs.
            // envExplicit keeps the two apart: changing defaultEnv moves every
            // inherited row at once, and that has to stay visible.
            const explicit = Object.prototype.hasOwnProperty.call(envMap, id);
            let env;
            let envSource;
            if (explicit) {
              env = this.normalizeEnv(envMap[id]) || String(envMap[id]);
              envSource = "ตั้งไว้เอง";
            } else if (this.raw.defaultEnv) {
              env = this.normalizeEnv(this.raw.defaultEnv) || "unknown";
              envSource = "จาก defaultEnv";
            } else {
              env = "unknown";
              envSource = "จาก ETAX_LAO_ENV ของ server";
            }

            return {
              companyID: id,
              nameTH,
              nameEN,
              env,
              envExplicit: explicit,
              envSource,
              docTypes: Object.prototype.hasOwnProperty.call(dtMap, id)
                ? this.normalizeList(dtMap[id])
                : null,
              extraColumns: Object.prototype.hasOwnProperty.call(ecMap, id)
                ? ecMap[id]
                : null,
              exportEnabled: exportEnabledFor(this.raw, id).enabled,
              exportExplicit: Object.prototype.hasOwnProperty.call(
                this.raw.exportEnabled || {},
                id
              ),
              autoIssue: apiAutoIssueFor(this.raw, id).enabled,
              autoIssueExplicit: Object.prototype.hasOwnProperty.call(
                this.raw.apiAutoIssue || {},
                id
              ),
            };
          })
        );
        this.rows = rows;
      } catch (e) {
        this.error = (e && e.message) || String(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
